import React from 'react';
import ConnectionQualityIndicator from './ConnectionQualityIndicator';
import ConnectionTypeIndicator, { DisplayMode } from './ConnectionTypeIndicator';
import BandwidthChart from './BandwidthChart';
import { ConnectionMode, VPNSessionInfo } from '../api/vpn';
import { formatBytes, formatDuration } from '../utils/formatting';

interface SessionItemProps {
  session: VPNSessionInfo;
  variant: 'hosted' | 'client';
  onDisconnect: (sessionId: string) => void;
}

/**
 * SessionItem component for displaying VPN session information
 *
 * Displays session details including:
 * - Participant names (host/client)
 * - Session name and duration
 * - Bandwidth usage
 * - Connection status and type
 * - Real-time bandwidth charts and quality indicators
 *
 * Supports two variants:
 * - 'hosted': Green styling for sessions you are hosting
 * - 'client': Blue styling for sessions you are connected to
 */
const SessionItem: React.FC<SessionItemProps> = ({ session, variant, onDisconnect }) => {
  const isHosted = variant === 'hosted';

  // Variant-specific styling
  const borderColor = isHosted ? 'border-green-200' : 'border-blue-200';
  const bgColor = isHosted ? 'bg-green-50' : 'bg-blue-50';
  const textColor = isHosted ? 'text-green-800' : 'text-primary-800';
  const secondaryTextColor = isHosted ? 'text-green-600' : 'text-primary-600';
  const statusBgColor = session.status === 'active'
    ? (isHosted ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-primary-800')
    : 'bg-yellow-100 text-yellow-800';

  // Display name based on variant
  const participantLabel = isHosted ? 'Hosting' : 'Connected to';
  const participantName = isHosted ? session.client_display_name : session.host_display_name;

  return (
    <div className={`border ${borderColor} ${bgColor} rounded-lg p-4`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex-1">
          <div className={`text-sm font-medium ${textColor}`}>
            {participantLabel}: {participantName}
          </div>
          <div className={`text-xs ${secondaryTextColor}`}>
            {session.session_name && `${session.session_name} • `}
            {formatDuration(session.duration_seconds)} •
            ↓{formatBytes(session.bytes_downloaded)} ↑{formatBytes(session.bytes_uploaded)}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusBgColor}`}>
            {session.status}
          </span>
          {session.connection_mode && session.status === 'active' && (
            <ConnectionTypeIndicator
              connectionType={session.connection_mode as ConnectionMode}
              mode={DisplayMode.COMPACT}
              showTooltip={true}
            />
          )}
          {session.status === 'active' && (
            <button
              onClick={() => onDisconnect(session.session_id)}
              className="px-3 py-2 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 transition-colors duration-200 rounded min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              Disconnect
            </button>
          )}
        </div>
      </div>
      {session.status === 'active' && session.started_at && (
        <div className="mt-4 space-y-3">
          <div className="flex justify-end">
            <ConnectionQualityIndicator
              sessionId={session.session_id}
              bytesUploaded={session.bytes_uploaded}
              bytesDownloaded={session.bytes_downloaded}
              startedAt={new Date(session.started_at)}
            />
          </div>
          <BandwidthChart
            sessionId={session.session_id}
            bytesUploaded={session.bytes_uploaded}
            bytesDownloaded={session.bytes_downloaded}
          />
        </div>
      )}
    </div>
  );
};

export default SessionItem;
